import { z } from "zod"
import {
  FreshServiceProposal,
  ServiceProposalManualAction,
  ServiceProposalPackage,
  ServiceProposalPackageSchema,
  ServiceProposalPartAction,
  SubmitFreshServiceProposal,
  SubmitFreshServiceProposalSchema,
} from "./fresh-service-proposal"
import { PartActionUrgency } from "./part-action-urgency"
import { buildDefaultSelection } from "./proposal-default-selection"
import { ProposalLevel } from "./proposal-level-kind"
import { ServiceAction } from "./service-part-action"
import {
  findProposalPartAction,
  getSelectedPartIds,
} from "./service-proposal-grouping"

/**
 * One way to apply a level: the selection a click puts in place, and the
 * packages the level shows alongside it. Packages are matched to the level's
 * components the way the optimizer does; where the proposal's own copy can
 * serve the level it is used, so the snapshot stays the one that was sent.
 * Manual actions, product decisions and Tillval are left empty: the customer's
 * own are kept.
 */
export const ProposalLevelOptionSchema = z.object({
  selection: SubmitFreshServiceProposalSchema,
  packages: z.array(ServiceProposalPackageSchema),
})
export type ProposalLevelOption = z.infer<typeof ProposalLevelOptionSchema>

/**
 * One level, worked out by the API. The only thing that can change which
 * packages win is whether the proposal's own wash is bought — so there is one
 * option with it and one without.
 */
export const ProposalLevelPresetSchema = z.object({
  /** Null when the proposal has no wash of its own. */
  withWash: ProposalLevelOptionSchema.nullable(),
  withoutWash: ProposalLevelOptionSchema,
})
export type ProposalLevelPreset = z.infer<typeof ProposalLevelPresetSchema>

/**
 * Both levels, sent with the proposal so switching between them is instant.
 */
export const ProposalLevelPresetsSchema = z.object({
  levels: z.object({
    [ProposalLevel.TRAFFIC_SAFE]: ProposalLevelPresetSchema,
    [ProposalLevel.TOP_CONDITION]: ProposalLevelPresetSchema,
  }),
})
export type ProposalLevelPresets = z.infer<typeof ProposalLevelPresetsSchema>

/** Every package either level shows, with the wash bought or not. */
export const getLevelPresetPackages = (
  presets: ProposalLevelPresets,
): ServiceProposalPackage[] =>
  Object.values(presets.levels)
    .flatMap((preset) => [preset.withWash, preset.withoutWash])
    .flatMap((option) => (option ? option.packages : []))

/**
 * The components a level works on. Work the customer cannot decline —
 * mandatory, or already approved at the reception — is in both levels.
 */
/**
 * Components a reception-approved package carries. The lock lives on the
 * package, not on the top-level copy of its actions.
 */
const getReceptionApprovedPackagePartIds = (
  proposal: FreshServiceProposal,
): Set<string> =>
  new Set(
    proposal.packages
      .filter((servicePackage) => servicePackage.isApprovedViaReception)
      .flatMap((servicePackage) =>
        servicePackage.partActions.map((action) => action.partId),
      ),
  )

export const getProposalLevelPartIds = (
  proposal: FreshServiceProposal,
  level: ProposalLevel,
): Set<string> => {
  const receptionApprovedPackagePartIds =
    getReceptionApprovedPackagePartIds(proposal)

  return new Set(
    proposal.partActions
      .filter((action) => action.action !== ServiceAction.DO_NOTHING)
      .filter(
        (action) =>
          level === ProposalLevel.TOP_CONDITION ||
          action.urgency === PartActionUrgency.NOW ||
          action.isMandatory ||
          action.isApprovedViaReception ||
          receptionApprovedPackagePartIds.has(action.partId),
      )
      .map((action) => action.partId),
  )
}

/**
 * False when there is no real choice to offer: both levels would contain the
 * same work, or Trafiksäker would hold nothing the customer could decline
 * (everything optional can wait), so it would only be the mandatory work.
 */
export const hasProposalLevels = (proposal: FreshServiceProposal): boolean => {
  const trafficSafePartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TRAFFIC_SAFE,
  )
  const receptionApprovedPackagePartIds =
    getReceptionApprovedPackagePartIds(proposal)
  const hasDeclinableUrgentWork = proposal.partActions.some(
    (action) =>
      trafficSafePartIds.has(action.partId) &&
      action.urgency === PartActionUrgency.NOW &&
      !action.isMandatory &&
      !action.isApprovedViaReception &&
      !receptionApprovedPackagePartIds.has(action.partId),
  )
  return (
    hasDeclinableUrgentWork &&
    trafficSafePartIds.size <
      getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION).size
  )
}

/**
 * The level a selection answers. Selections saved before the levels existed
 * read as Toppskick, the level everything used to be.
 */
export const getSelectionLevel = (
  values: SubmitFreshServiceProposal,
): ProposalLevel => values.level ?? ProposalLevel.TOP_CONDITION

/**
 * Another wash alternative than the proposal's own is never in a package, so
 * it stays standalone whatever the level picks.
 */
const getPickedAlternativeWashId = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): string | undefined =>
  values.selectedWashOriginalId !== undefined &&
  values.selectedWashOriginalId !== proposal.wash?.originalWashId
    ? values.selectedWashOriginalId
    : undefined

/** The wash the customer is buying picks which of a level's options applies. */
const getLevelOption = (
  preset: ProposalLevelPreset,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): ProposalLevelOption => {
  const buysProposalWash =
    getPickedAlternativeWashId(proposal, values) === undefined &&
    (values.selectedWashOriginalId !== undefined ||
      values.selectedPackages.some(
        (selectedPackage) => selectedPackage.selectedWashOriginalId,
      ))
  return buysProposalWash && preset.withWash
    ? preset.withWash
    : preset.withoutWash
}

/**
 * The work the customer took out of a level, for its card to list struck
 * through. A component only a package carries is read off that package.
 */
export const getRemovedLevelPartActions = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
  level: ProposalLevel,
): ServiceProposalPartAction[] => {
  const levelPartIds = getProposalLevelPartIds(proposal, level)
  const actions = new Map<string, ServiceProposalPartAction>()
  for (const servicePackage of proposal.packages) {
    for (const action of servicePackage.partActions) {
      actions.set(action.partId, action)
    }
  }
  for (const action of proposal.partActions) actions.set(action.partId, action)

  return [...getRemovedPartIds(proposal, values)]
    .filter((partId) => levelPartIds.has(partId))
    .flatMap((partId) => {
      const action = actions.get(partId)
      return action ? [action] : []
    })
}

/**
 * Components only "Toppskick" works on: the mechanic judged they can wait, so
 * they are the preventive work on top of what makes the bike road-safe.
 */
const getPreventivePartIds = (proposal: FreshServiceProposal): Set<string> => {
  const trafficSafePartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TRAFFIC_SAFE,
  )
  return new Set(
    [...getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION)].filter(
      (partId) => !trafficSafePartIds.has(partId),
    ),
  )
}

/** One action a level holds, as its card and its "Läs mer" sheet list it. */
export type ProposalLevelWork = {
  key: string
  /** Can wait, as opposed to urgent. */
  isPreventive: boolean
  /** Part of the level, but taken out by the customer. */
  isRemoved: boolean
} & (
  | { kind: "PART"; action: ServiceProposalPartAction }
  | { kind: "MANUAL"; action: ServiceProposalManualAction }
)

interface ProposalLevelWorkInput {
  /** The proposal as sent, with both levels' work. */
  proposal: FreshServiceProposal
  /** The proposal this level answers, from `getLevelProposal`. */
  levelProposal: FreshServiceProposal
  level: ProposalLevel
  /** What the level buys. */
  levelValues: SubmitFreshServiceProposal
  /** The customer's selection, which says what they took out. */
  values: SubmitFreshServiceProposal
}

/**
 * Every action in a level, one by one: a package is listed as the work in it.
 * Work the customer took out comes last. Washes and fees are not actions, and
 * manual actions are in both levels, so they count as urgent.
 */
export const getProposalLevelWork = ({
  proposal,
  levelProposal,
  level,
  levelValues,
  values,
}: ProposalLevelWorkInput): ProposalLevelWork[] => {
  const preventivePartIds = getPreventivePartIds(proposal)
  const toPartWork = (
    action: ServiceProposalPartAction,
    isRemoved: boolean,
  ): ProposalLevelWork => ({
    kind: "PART",
    key: `part-${action.partId}`,
    action,
    isPreventive: preventivePartIds.has(action.partId),
    isRemoved,
  })

  const selectedManualIds = new Set(
    levelValues.selectedManualActions.map((selected) => selected.id),
  )

  return [
    ...[...getSelectedPartIds(levelValues)]
      .flatMap((partId) => {
        const action = findProposalPartAction(levelProposal, partId)
        return action && action.action !== ServiceAction.DO_NOTHING
          ? [action]
          : []
      })
      .map((action) => toPartWork(action, false)),
    ...levelProposal.manualActions
      .filter((action) => selectedManualIds.has(action.id))
      .map(
        (action): ProposalLevelWork => ({
          kind: "MANUAL",
          key: `manual-${action.id}`,
          action,
          isPreventive: false,
          isRemoved: false,
        }),
      ),
    ...getRemovedLevelPartActions(proposal, values, level).map((action) =>
      toPartWork(action, true),
    ),
  ]
}

/** A level's selection without the given components. */
const withoutParts = (
  selection: SubmitFreshServiceProposal,
  removedPartIds: ReadonlySet<string>,
): SubmitFreshServiceProposal => ({
  ...selection,
  selectedPartActions: selection.selectedPartActions.filter(
    (selected) => !removedPartIds.has(selected.partId),
  ),
  selectedPackages: selection.selectedPackages.flatMap((selectedPackage) => {
    const selectedPartActions = selectedPackage.selectedPartActions.filter(
      (selected) => !removedPartIds.has(selected.partId),
    )
    // A package with all of its work taken out is not bought.
    if (
      selectedPartActions.length === 0 &&
      selectedPackage.selectedPartActions.length > 0
    ) {
      return []
    }
    return [{ ...selectedPackage, selectedPartActions }]
  }),
})

/** The level's own selection, with what it has no say in carried over. */
const getProposalLevelPresetSelection = (
  presets: ProposalLevelPresets,
  level: ProposalLevel,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const levelSelection = getLevelOption(
    presets.levels[level],
    proposal,
    values,
  ).selection

  return {
    ...levelSelection,
    selectedWashOriginalId:
      getPickedAlternativeWashId(proposal, values) ??
      levelSelection.selectedWashOriginalId,
    selectedManualActions: values.selectedManualActions,
    isWashDeselected: values.isWashDeselected,
    selectedUpsellProductIds: values.selectedUpsellProductIds,
    hasCompletedServiceStep: values.hasCompletedServiceStep,
    level,
  }
}

/**
 * The components the customer actively took out: the ones remembered from
 * the other level, plus those the current level works on that they are not
 * buying. A component bought again is no longer removed. Both levels are
 * matched to packages without them.
 */
export const getRemovedPartIds = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const selectedPartIds = getSelectedPartIds(values)
  const levelPartIds = getProposalLevelPartIds(
    proposal,
    getSelectionLevel(values),
  )
  return new Set(
    [...(values.removedPartIds ?? []), ...levelPartIds].filter(
      (partId) => !selectedPartIds.has(partId),
    ),
  )
}

/**
 * Carries the customer's product choices over to a configuration the optimizer
 * sent back, or a level click is about to apply. The optimizer decides which packages and actions are in the
 * selection, never which product goes inside them: it answers every choice with
 * the mechanic's recommendation. Applied as-is that would overwrite an
 * alternative the customer picked a second earlier, and mark choices they never
 * opened as decided — so the incoming configuration keeps its shape, and every
 * product decision comes from what the customer had actually chosen.
 *
 * Decisions are looked up per action rather than per location, so a part moving
 * out of a package keeps the product picked for it.
 */
export const withCustomerProductDecisions = (
  configuration: SubmitFreshServiceProposal,
  previous: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const decisionsByActionId = new Map<string, Record<string, string>>()

  const remember = (
    actionId: string,
    productDecisions: Record<string, string>,
  ): void => {
    if (Object.keys(productDecisions).length === 0) return
    decisionsByActionId.set(actionId, productDecisions)
  }

  for (const action of previous.selectedPartActions) {
    remember(action.partId, action.productDecisions)
  }
  for (const action of previous.selectedManualActions) {
    remember(action.id, action.productDecisions)
  }
  for (const selectedPackage of previous.selectedPackages) {
    for (const action of selectedPackage.selectedPartActions) {
      remember(action.partId, action.productDecisions)
    }
  }

  const decisionsFor = (actionId: string): Record<string, string> =>
    decisionsByActionId.get(actionId) ?? {}

  return {
    ...configuration,
    selectedPackages: configuration.selectedPackages.map((selectedPackage) => ({
      ...selectedPackage,
      selectedPartActions: selectedPackage.selectedPartActions.map(
        (action) => ({
          ...action,
          productDecisions: decisionsFor(action.partId),
        }),
      ),
    })),
    selectedPartActions: configuration.selectedPartActions.map((action) => ({
      ...action,
      productDecisions: decisionsFor(action.partId),
    })),
    selectedManualActions: configuration.selectedManualActions.map(
      (action) => ({
        ...action,
        productDecisions: decisionsFor(action.id),
      }),
    ),
  }
}

/**
 * The selection a level click applies. The level decides packages and part
 * actions; everything it has no say in is carried over, and so are the
 * components the customer took out — a removal holds in both levels. The API
 * matches the presets without them; they are filtered here too because the
 * presets lag a removal by one optimizer run.
 */
export const getProposalLevelSelection = (
  presets: ProposalLevelPresets,
  level: ProposalLevel,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const removedPartIds = getRemovedPartIds(proposal, values)
  // The preset answers no product choice; the customer's picks are kept so
  // the level is priced, and applied, with them.
  return withCustomerProductDecisions(
    {
      ...withoutParts(
        getProposalLevelPresetSelection(presets, level, proposal, values),
        removedPartIds,
      ),
      removedPartIds: [...removedPartIds],
    },
    values,
  )
}

/**
 * The proposal the customer is answering. Toppskick and Trafiksäker are two
 * proposals, not two selections on one: work and packages only the other
 * level has are not listed at all, so nothing reads as "Borttagen" that the
 * customer never removed.
 *
 * Packages the selection holds are always listed, wherever they came from —
 * the optimizer can swap one in, and buying the wash moves the level to its
 * other option.
 */
export const getLevelProposal = (
  proposal: FreshServiceProposal,
  presets: ProposalLevelPresets,
  values: SubmitFreshServiceProposal,
): FreshServiceProposal => {
  const level = getSelectionLevel(values)
  const preset = presets.levels[level]
  const selectedPackageIds = new Set(
    values.selectedPackages.map(
      (selectedPackage) => selectedPackage.originalPackageId,
    ),
  )
  const isSelected = (servicePackage: ServiceProposalPackage): boolean =>
    selectedPackageIds.has(servicePackage.originalPackageId)

  const packages = new Map<string, ServiceProposalPackage>()
  const candidates = [
    // Agreed at drop-off, so part of both levels.
    ...proposal.packages.filter(
      (servicePackage) => servicePackage.isApprovedViaReception,
    ),
    ...getLevelOption(preset, proposal, values).packages,
    ...[preset.withWash, preset.withoutWash]
      .flatMap((option) => (option ? option.packages : []))
      .filter(isSelected),
    ...proposal.packages.filter(isSelected),
  ]
  for (const servicePackage of candidates) {
    if (!packages.has(servicePackage.originalPackageId)) {
      packages.set(servicePackage.originalPackageId, servicePackage)
    }
  }

  const levelPartIds = getProposalLevelPartIds(proposal, level)
  return {
    ...proposal,
    packages: [...packages.values()],
    partActions: proposal.partActions.filter(
      (action) =>
        // DO_NOTHING entries only carry the mechanic's comment.
        action.action === ServiceAction.DO_NOTHING ||
        levelPartIds.has(action.partId),
    ),
  }
}

/**
 * Where an unanswered proposal starts: Toppskick unless an A/B test says
 * otherwise, with the packages that level matched. The proposal's own
 * defaults can select a package the level did not pick, which would list the
 * level's package beside it as "Borttagen".
 */
export const buildLevelDefaultSelection = (
  proposal: FreshServiceProposal,
  presets: ProposalLevelPresets | null,
  /**
   * The level to start on; an A/B test can start on Trafiksäker. Null starts
   * on none: Toppskick's work, with no level stored until the cyclist picks
   * one.
   */
  defaultLevel: ProposalLevel | null = ProposalLevel.TOP_CONDITION,
): SubmitFreshServiceProposal => {
  const defaults = buildDefaultSelection(proposal)
  if (!presets) return defaults
  const selection = getProposalLevelPresetSelection(
    presets,
    defaultLevel ?? ProposalLevel.TOP_CONDITION,
    proposal,
    defaults,
  )
  return defaultLevel === null ? { ...selection, level: undefined } : selection
}
